import React from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

const highlights = [
  { 
    number: '01',
    text: 'Proximity to Navi Mumbai International Airport (NMIA)', 
    detail: 'The entire region is being developed around the upcoming NMIA, a mega infrastructure project expected to drive real estate, commercial, and industrial growth.',
    accent: 'bg-primary-600',
  },
  { 
    number: '02',
    text: 'Government-Backed Planned Development', 
    detail: 'Development by MMRDA with planned zones for residential, commercial, industrial, and logistics. Emphasis on sustainable urban planning.',
    accent: 'bg-accent-500',
  },
  { 
    number: '03',
    text: 'Affordable Entry Point', 
    detail: 'Compared to Mumbai and even Navi Mumbai, land prices are currently much lower, providing high appreciation potential for early investors.',
    accent: 'bg-primary-600',
  },
  { 
    number: '04',
    text: 'Employment and Economic Zones', 
    detail: 'Plans for IT parks, logistics hubs, education zones, and industrial parks will attract companies and create jobs, driving long-term housing demand.',
    accent: 'bg-accent-500',
  },
];

const Highlights = () => {
  const [headRef, headVisible] = useScrollReveal();
  const [cardsRef, cardsVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="highlights" className="py-24 lg:py-32 relative overflow-hidden bg-gray-100 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header — left aligned */}
        <div ref={headRef} className={`max-w-2xl mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-5">
            Investment Potential
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-dark-800 leading-tight mb-5">
            Why MahaMumbai Is Best{' '}
            <span className="italic text-primary-600">For Investment?</span>
          </h2>
          <p className="text-dark-400 text-base leading-relaxed font-light">
            The Third MahaMumbai is one of the best upcoming investment destinations in India
            for several compelling reasons.
          </p>
        </div>

        {/* Stacked horizontal cards */}
        <div ref={cardsRef} className="space-y-4">
          {highlights.map((item, i) => (
            <div
              key={i}
              className={`group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-700 ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: cardsVisible ? `${i * 100}ms` : '0ms' }}
            >
              <div className="flex items-stretch">
                {/* Number block */}
                <div className={`hidden sm:flex w-24 lg:w-28 flex-shrink-0 ${item.accent} items-center justify-center`}>
                  <span className="text-3xl lg:text-4xl font-heading text-white/90">{item.number}</span>
                </div>
                {/* Content */}
                <div className="flex-1 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 sm:hidden">
                      <span className={`${item.accent} text-white text-xs font-heading w-8 h-8 rounded-lg flex items-center justify-center`}>{item.number}</span>
                      <h3 className="font-heading text-lg text-dark-800 leading-snug">{item.text}</h3>
                    </div>
                    <h3 className="hidden sm:block font-heading text-lg lg:text-xl text-dark-800 leading-snug mb-2 group-hover:text-primary-700 transition-colors duration-500">
                      {item.text}
                    </h3>
                    <p className="text-[13px] text-dark-400 font-light leading-relaxed max-w-2xl">
                      {item.detail}
                    </p>
                  </div>
                  <svg className="hidden lg:block w-5 h-5 text-gray-200 group-hover:text-primary-400 transition-all duration-500 flex-shrink-0 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <button onClick={() => window.dispatchEvent(new Event('openContactModal'))} className="inline-flex items-center gap-2.5 bg-primary-600 text-white rounded-full px-7 py-3 text-[11px] font-semibold tracking-wider uppercase hover:bg-primary-700 hover:shadow-lg transition-all duration-500 group">
            View More Details
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <a href="/Brochure.pdf" download className="inline-flex items-center gap-2 border-2 border-dark-700 text-dark-700 rounded-full px-7 py-3 text-[11px] font-semibold tracking-wider uppercase hover:bg-dark-700 hover:text-white transition-all duration-500">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Download Brochure
          </a>
        </div>
      </div>
    </section>
  );
};

export default Highlights;
